const COMPACT_THRESHOLDS = [
  { divisor: 1e12, suffix: 'T' },
  { divisor: 1e9, suffix: 'B' },
  { divisor: 1e6, suffix: 'M' },
  { divisor: 1e3, suffix: 'K' },
] as const;

const LOCALE = 'en-US';

/**
 * Formats a large number with a compact alpha suffix (K, M, B, T).
 * Pass { currency: true } to prefix with '$'.
 *
 * formatCompact(4267411285800)                    → "4.27T"
 * formatCompact(4267411285800, { currency: true }) → "$4.27T"
 * formatCompact(50076029.93)                      → "50.08M"
 */
export function formatCompact(
  value: number | null | undefined,
  options?: { currency?: boolean },
): string | undefined {
  if (value == null) {
    return undefined;
  }
  const prefix = options?.currency ? '$' : '';
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  for (const { divisor, suffix } of COMPACT_THRESHOLDS) {
    if (abs >= divisor) {
      return `${sign}${prefix}${(abs / divisor).toFixed(2)}${suffix}`;
    }
  }
  return `${sign}${prefix}${abs.toFixed(2)}`;
}

/**
 * Formats a large number with a compact alpha suffix and a '$' prefix.
 * Convenience wrapper over formatCompact for currency-denominated values.
 *
 * formatCompactCurrency(3.4e12) → "$3.40T"
 * formatCompactCurrency(6.2e9)  → "$6.20B"
 */
export function formatCompactCurrency(
  value: number | null | undefined,
): string | undefined {
  return formatCompact(value, { currency: true });
}

/**
 * Formats a number as a dollar amount with commas and 2 decimal places.
 *
 * formatCurrency(293.2)    → "$293.20"
 * formatCurrency(23923.23) → "$23,923.23"
 */
export function formatCurrency(
  value: number | null | undefined,
): string | undefined {
  if (value == null) {
    return undefined;
  }
  return value.toLocaleString(LOCALE, { style: 'currency', currency: 'USD' });
}

/**
 * Formats a number with commas and 2 decimal places, no prefix or suffix.
 *
 * Pass { roundingMode } to override the default half-up rounding. Use 'trunc'
 * (toward zero) where rounding past an integer boundary would misrepresent the
 * value — e.g. a beta of 0.998 must read "0.99", not "1.00", so it stays
 * visibly below 1.
 *
 * formatDecimal(48.963)                            → "48.96"
 * formatDecimal(1293.2)                            → "1,293.20"
 * formatDecimal(0.998, { roundingMode: 'trunc' })  → "0.99"
 */
export function formatDecimal(
  value: number | null | undefined,
  options?: { roundingMode?: Intl.NumberFormatOptions['roundingMode'] },
): string | undefined {
  if (value == null) {
    return undefined;
  }
  return value.toLocaleString(LOCALE, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    roundingMode: options?.roundingMode,
  });
}

/**
 * Formats a whole number with commas, no decimal places.
 *
 * formatInteger(166000) → "166,000"
 * formatInteger(36000)  → "36,000"
 */
export function formatInteger(
  value: number | null | undefined,
): string | undefined {
  if (value == null) {
    return undefined;
  }
  return value.toLocaleString(LOCALE, { maximumFractionDigits: 0 });
}

/**
 * Formats an ISO 8601 date string as MM/DD/YYYY in UTC.
 *
 * formatMonthDayYearDate('2025-12-12T14:16:00Z') → "12/12/2025"
 */
export function formatMonthDayYearDate(
  value: string | null | undefined,
): string | undefined {
  if (!value) {
    return undefined;
  }
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return undefined;
  }

  return new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

/**
 * Formats a number as a percentage with 2 decimal places.
 * Pass { isRatio: true } when the value is a decimal ratio (e.g. 0.003614 → "0.36%").
 * Without the option the value is treated as already a percent (e.g. 128.15 → "128.15%").
 *
 * formatPercent(2.87)                         → "2.87%"
 * formatPercent(0.003614, { isRatio: true })  → "0.36%"
 * formatPercent(128.15)                       → "128.15%"
 */
export function formatPercent(
  value: number | null | undefined,
  options?: { isRatio?: boolean },
): string | undefined {
  if (value == null) {
    return undefined;
  }
  const pct = options?.isRatio ? value * 100 : value;
  return `${pct.toFixed(2)}%`;
}

/**
 * Rounds a number to 2 decimal places, returning a number.
 * Use when a rounded number (not string) is required — e.g. the Indicator component's
 * value prop, where the component handles its own string rendering.
 *
 * roundDecimal(128.15076560659602) → 128.15
 * roundDecimal(1.05161)            → 1.05
 */
export function roundDecimal(
  value: number | null | undefined,
): number | undefined {
  if (value == null) {
    return undefined;
  }
  return parseFloat(value.toFixed(2));
}
